// src/components/Events/Tree/TreeCanvas.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Popover, Typography, Tooltip, Button } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import SnowLayer from './SnowLayer';
import DraggableDecoration from './DraggableDecoration';

// Django media 경로 보정
function resolveMediaUrl(url) {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return url; // 보통 /media/... 형태
}

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
// ✅ TreePage와 동일하게: 밖으로도 배치 허용
const clampRange = (v, min = -0.3, max = 1.3) => clamp(v, min, max);

// ✅ object-fit: cover 매핑(기본 objectPosition: 50% 50%)
const getCoverMap = (boxW, boxH, imgW, imgH) => {
    if (!boxW || !boxH || !imgW || !imgH) return null;

    const scale = Math.max(boxW / imgW, boxH / imgH);
    const drawW = imgW * scale;
    const drawH = imgH * scale;

    // 중앙 정렬(50% 50%) 기준 오프셋(= 크롭 오프셋)
    const ox = (boxW - drawW) / 2;
    const oy = (boxH - drawH) / 2;

    return { scale, drawW, drawH, ox, oy };
};

export default function TreeCanvas({
    event,
    decorations = [],
    pendingPreview = null, // { x, y, item }
    height = 640, // (호환용) 필요하면 외부에서 내려줄 수 있음 (현재는 aspectRatio 사용)
    dropDisabled = false,
    outerRef,
    myDecorationId,
    bgNatural, // ✅ { w, h } - TreePage에서 로드한 bg natural size
}) {
    const wrapRef = useRef(null);
    const [wrapSize, setWrapSize] = useState({ w: 0, h: 0 });

    const { setNodeRef: setDropRef } = useDroppable({
        id: 'tree-drop-zone',
        disabled: dropDisabled,
    });

    const [popover, setPopover] = useState({
        open: false,
        anchorPosition: { top: 0, left: 0 },
        decoration: null,
    });

    // ✅ (fallback용) 고정 씬 비율 (원본 배경 1600x1200 = 4:3)
    const SCENE_AR = 4 / 3;
    function getSceneRect(w, h) {
        if (!w || !h) return { x: 0, y: 0, w: 0, h: 0 };
        const boxAR = w / h;

        let sceneW, sceneH;
        if (boxAR > SCENE_AR) {
            sceneH = h;
            sceneW = h * SCENE_AR;
        } else {
            sceneW = w;
            sceneH = w / SCENE_AR;
        }

        return {
            x: (w - sceneW) / 2,
            y: (h - sceneH) / 2,
            w: sceneW,
            h: sceneH,
        };
    }

    const setRefs = (node) => {
        wrapRef.current = node;
        setDropRef(node);
        if (outerRef) outerRef.current = node;
    };

    // 캔버스 사이즈 추적
    useEffect(() => {
        if (!wrapRef.current) return;

        const ro = new ResizeObserver((entries) => {
            const rect = entries[0]?.contentRect;
            if (!rect) return;
            setWrapSize({ w: rect.width, h: rect.height });
        });

        ro.observe(wrapRef.current);
        return () => ro.disconnect();
    }, []);

    const bgUrl = useMemo(() => resolveMediaUrl(event?.bg_image), [event]);
    const treeUrl = useMemo(() => resolveMediaUrl(event?.tree_image), [event]);

    // ✅ 모바일/데스크탑 모두 안정적인 포인터 좌표
    const getPointerXYFromEvent = (e) => {
        if (!e) return null;

        const point = e?.touches?.[0] || e?.changedTouches?.[0];
        const x = typeof e.clientX === 'number' ? e.clientX : point?.clientX;
        const y = typeof e.clientY === 'number' ? e.clientY : point?.clientY;

        if (typeof x === 'number' && typeof y === 'number') return { x, y };
        return null;
    };

    const handleDecoClick = (e, deco) => {
        e.stopPropagation();
        const p = getPointerXYFromEvent(e);
        if (!p) return;

        setPopover({
            open: true,
            anchorPosition: { top: p.y, left: p.x },
            decoration: deco,
        });
    };

    const closePopover = () => {
        setPopover((prev) => ({ ...prev, open: false }));
    };

    const getDisplayName = (deco) =>
        deco?.user_nickname ||
        deco?.nickname ||
        deco?.username ||
        deco?.user?.username ||
        '익명';

    const ready = wrapSize.w > 0 && wrapSize.h > 0;

    // ✅ cover 맵 (배경 natural size 필요)
    const coverMap = useMemo(() => {
        if (!ready) return null;
        if (!bgNatural?.w || !bgNatural?.h) return null;
        return getCoverMap(wrapSize.w, wrapSize.h, bgNatural.w, bgNatural.h);
    }, [ready, wrapSize.w, wrapSize.h, bgNatural?.w, bgNatural?.h]);

    // ✅ fallback scene (이전 좌표/혹은 bgNatural 없을 때)
    const scene = getSceneRect(wrapSize.w, wrapSize.h);

    // ✅ 아이템 크기 기준 (scene 대신 전체 박스 기반이 더 안정적)
    const baseSize = useMemo(() => {
        const base = Math.min(wrapSize.w || 0, wrapSize.h || 0);
        const ratio = 0.07;
        return clamp(base * ratio, 26, 72);
    }, [wrapSize.w, wrapSize.h]);

    // ✅ (핵심) x01,y01 → 화면 px 변환 (cover 기준)
    const image01ToPx = (x01, y01) => {
        // coverMap이 있으면 cover 기준
        if (coverMap) {
            return {
                left: coverMap.ox + coverMap.drawW * x01,
                top: coverMap.oy + coverMap.drawH * y01,
            };
        }

        // fallback: scene 기준(이전 방식)
        return {
            left: scene.x + scene.w * x01,
            top: scene.y + scene.h * y01,
        };
    };

    // ✅ 프리뷰 위치 계산 (정규화 → px)
    const preview = useMemo(() => {
        if (!ready) return null;
        if (!pendingPreview?.item) return null;

        const x01 = clampRange(Number(pendingPreview.x) || 0);
        const y01 = clampRange(Number(pendingPreview.y) || 0);

        const { left, top } = image01ToPx(x01, y01);

        return {
            x01,
            y01,
            left,
            top,
            itemUrl: resolveMediaUrl(pendingPreview.item?.image),
            name: pendingPreview.item?.name || 'preview',
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingPreview, ready, coverMap, scene.x, scene.y, scene.w, scene.h]);

    return (
        <Box
            ref={setRefs}
            onClick={closePopover}
            sx={{
                position: 'relative',

                // ✅ 가로 스크롤 방지
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden',

                // ✅ PC/모바일 모두 동일 기준으로 4:3
                aspectRatio: '4 / 3',
                height: 'auto',

                minHeight: { xs: 320, sm: 360 },

                borderRadius: 2,
                backgroundColor: 'grey.100',
                userSelect: 'none',
                touchAction: 'manipulation', // 핀치줌 허용(드래그도 어느 정도 가능)

                boxShadow: dropDisabled ? 'none' : 'inset 0 0 0 2px rgba(0,0,0,0.12)',
            }}
        >
            {/* 배경 (cover) */}
            {bgUrl && (
                <Box
                    component="img"
                    src={bgUrl}
                    alt="tree background"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                    draggable={false}
                />
            )}

            {/* ❄️ 눈 레이어 */}
            {!!event && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 6,
                        pointerEvents: 'none',
                    }}
                >
                    <SnowLayer />
                </Box>
            )}

            {/* 트리 (별도 오버레이가 필요하면 유지 / 없으면 event.tree_image를 비우면 됨) */}
            {treeUrl && (
                <Box
                    component="img"
                    src={treeUrl}
                    alt="tree"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        pointerEvents: 'none',
                        zIndex: 5,
                    }}
                    draggable={false}
                />
            )}

            {/* 🎁 장식들 */}
            {ready &&
                decorations.map((deco) => {
                    const itemUrl = resolveMediaUrl(deco?.item?.image);

                    const x01 = clampRange(Number(deco.x) || 0);
                    const y01 = clampRange(Number(deco.y) || 0);

                    const { left, top } = image01ToPx(x01, y01);

                    const scale = Number(deco.scale) || 1;
                    const rotate = Number(deco.rotate) || 0;

                    const decoZ = Number.isFinite(Number(deco.z_index)) ? Number(deco.z_index) : 1;
                    const isMine = deco.id === myDecorationId;
                    const finalZ = 60 + decoZ;
                    const displayName = getDisplayName(deco);

                    return (
                        <Tooltip
                            key={deco.id}
                            arrow
                            placement="top"
                            title={<Box sx={{ fontSize: 12, fontWeight: 600 }}>{displayName}</Box>}
                        >
                            {/* ✅ 1) "좌표/센터링"만 담당 */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left,
                                    top,
                                    width: baseSize,
                                    height: baseSize,
                                    transform: 'translate(-50%, -50%)',
                                    transformOrigin: 'center',
                                    zIndex: finalZ,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {/* ✅ 2) 드래그 레이어는 scale/rotate 영향 안 받음 */}
                                <DraggableDecoration id={`deco:${deco.id}`} disabled={!isMine || dropDisabled}>
                                    {/* ✅ 3) scale/rotate는 안쪽 레이어 */}
                                    <Box
                                        onClick={(e) => handleDecoClick(e, deco)}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            touchAction: 'none', // ✅ 드래그 대상에서만 제스처 차단
                                            transform: `scale(${scale}) rotate(${rotate}deg)`,
                                            transformOrigin: 'center',
                                            cursor: isMine && !dropDisabled ? 'grab' : 'pointer',
                                            filter: isMine ? 'drop-shadow(0 0 6px rgba(255,215,0,0.9))' : 'none',
                                            animation: isMine ? 'treeGlow 2s ease-in-out infinite alternate' : 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {itemUrl ? (
                                            <Box
                                                component="img"
                                                src={itemUrl}
                                                alt={deco?.item?.name || 'decoration'}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                    pointerEvents: 'none',
                                                    userSelect: 'none',
                                                }}
                                                draggable={false}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: '50%',
                                                    bgcolor: 'grey.300',
                                                }}
                                            />
                                        )}
                                    </Box>
                                </DraggableDecoration>
                            </Box>
                        </Tooltip>
                    );
                })}

            {/* ✅ 드롭 직후 임시 프리뷰 */}
            {preview && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: preview.left,
                        top: preview.top,
                        width: baseSize,
                        height: baseSize,
                        transform: 'translate(-50%, -50%)',
                        zIndex: 9999,
                        opacity: 0.75,
                        pointerEvents: 'none',
                        filter: 'drop-shadow(0 0 6px rgba(0,0,0,0.35))',
                    }}
                >
                    {preview.itemUrl ? (
                        <Box
                            component="img"
                            src={preview.itemUrl}
                            alt={preview.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            draggable={false}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                bgcolor: 'grey.300',
                            }}
                        />
                    )}

                    <Box
                        sx={{
                            position: 'absolute',
                            inset: -6,
                            borderRadius: '50%',
                            border: '2px dashed rgba(0,0,0,0.35)',
                        }}
                    />
                </Box>
            )}

            {/* 메시지 팝오버 */}
            <Popover
                open={popover.open}
                onClose={closePopover}
                anchorReference="anchorPosition"
                anchorPosition={popover.anchorPosition}
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        p: 0,
                    },
                }}
            >
                {popover.decoration && (
                    <Box
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.92)',
                            borderRadius: 2,
                            px: 1.4,
                            py: 1.1,
                            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                            maxWidth: 240,
                        }}
                    >
                        <Typography variant="body2" sx={{ mb: 0.6, wordBreak: 'break-word' }}>
                            <strong>{getDisplayName(popover.decoration)}</strong>
                            {popover.decoration?.message?.trim()
                                ? ` : ${popover.decoration.message}`
                                : ' : 메시지가 없습니다.'}
                        </Typography>

                        {popover.decoration.id === myDecorationId && (
                            <Button
                                size="small"
                                variant="outlined"
                                fullWidth
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closePopover();
                                    window.dispatchEvent(
                                        new CustomEvent('tree-edit-message', {
                                            detail: popover.decoration,
                                        })
                                    );
                                }}
                            >
                                문구 수정
                            </Button>
                        )}
                    </Box>
                )}
            </Popover>

            {/* glow_toggle */}
            <style>
                {`
          @keyframes treeGlow {
            from { filter: drop-shadow(0 0 4px rgba(255,215,0,0.55)); }
            to   { filter: drop-shadow(0 0 10px rgba(255,215,0,1)); }
          }
        `}
            </style>
        </Box>
    );
}
